/**
 * „MI EZ?" — az ügyfél megmondja, mit töltött fel (`design/Tipus.dc.html`).
 *
 * ═══ AMI A MAKETTBŐL ÁTJÖTT, ÉS AMI NEM ═══
 *
 * ⚠️ A „SZERINTÜNK EZ" JAVASLAT KIMARADT. A makett kiemelt, előre kiválasztott
 * tippje („Üzemanyagszámla · MOL Nyrt. · 12 340 Ft") MEZŐKINYERÉSEN áll — az
 * az `OcrProvider`, a build-terv 7. lépése, és NINCS megépítve. Egy tipp
 * nélküli „Szerintünk ez" doboz vagy üres volna, vagy hazudna; a „Vagy
 * válassz" lista viszont önmagában is működik.
 *
 * ⚠️ A „EZT A KÖNYVELŐD ÁLLÍTOTTA BE NEKED" MONDAT SEM JÖTT ÁT. A cégenkénti
 * típus-engedély már megvan (#25/3) — de az ügyfél nem látja, mi lett
 * letiltva, csak azt, ami választható. Egy ígéret-mondat nélkül ez nem hiányzik.
 *
 * ⚠️ A „PARKOLÓJEGY, AUTÓPÁLYA" NEM KÜLÖN SOR. A makett külön kínálja, de a
 * `DocType` enumban nincs neki érték, és felvenni sem szabad csak ezért: két
 * választás UGYANOTT kötne ki, vagyis az ügyfél olyan megkülönböztetést tenne,
 * amit a rendszer eldob. A parkolójegy ezért a nyugta feliratába került.
 *
 * ═══ A „NEM TUDOM" NEM ÍR SEMMIT ═══
 *
 * A makett egyenrangú, szaggatott keretes menekülőútként rajzolja, és a saját
 * szövege mondja ki, hogy „ez is jó válasz". Nálunk ez azt jelenti, hogy a
 * kérés EL SEM MEGY: a `clientDocType` marad `NULL`. Egy külön „ismeretlen"
 * enum-érték hazudna — nem lehetne megkülönböztetni attól az ügyféltől, akit
 * meg sem kérdeztünk.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/lib/api-client';
import { declareDocType } from './declareDocType';
import { docTypeHint, docTypeLabel, offeredDocTypes } from './docTypeChoices';
import type { ClientDocType } from './types';

/**
 * A bukás mondata. A 409 és a 422 SAJÁT mondatot kap: „próbáld újra" mindkettőnél
 * hazugság volna — a könyvelő már elintézte, illetve a típus ennél a cégnél
 * nincs engedélyezve. Az újrapróbálás ugyanígy elbukna.
 */
function failureMessage(t: (key: string) => string, caught: unknown): string {
  if (caught instanceof ApiError && caught.status === 409) {
    return t('docType.tooLate');
  }
  if (caught instanceof ApiError && caught.status === 422) {
    return t('docType.notAllowed');
  }
  return t('docType.failed');
}

export function DocTypePicker({
  documentId,
  declared,
  allowed,
  onDeclared,
}: {
  documentId: string;
  /** Amit MÁR mondott, ha mondott. `null` = nem nyilatkozott. */
  declared: ClientDocType | null;
  /**
   * A cég engedélyezett típusai (#25/3). `null` = a szerver nem mondta meg —
   * ilyenkor mind felkínálva, és a tiltottat a szerver utasítja el.
   */
  allowed: readonly ClientDocType[] | null;
  onDeclared: (documentId: string, docType: ClientDocType) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (declared !== null) {
    // AMIT MÁR MEGMONDOTT, AZ ÁLLÍTÁS, NEM GOMB. Újranyitni nincs mit: a
    // könyvelő ettől kezdve ezt látja, és ha téves, ő javítja — az ügyfél
    // oda-vissza kapcsolgatása csak zajt termelne a munkalistán.
    return <span className="doctype-declared">{docTypeLabel(t, declared)}</span>;
  }

  if (!open) {
    return (
      <button type="button" className="pick pick-link doctype-ask" onClick={() => setOpen(true)}>
        {t('docType.ask')}
      </button>
    );
  }

  async function choose(docType: ClientDocType) {
    setBusy(true);
    setError(null);
    try {
      await declareDocType(documentId, docType);
      onDeclared(documentId, docType);
    } catch (caught) {
      setError(failureMessage(t, caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="doctype-choices">
      {offeredDocTypes(allowed).map((docType) => (
        <button
          key={docType}
          type="button"
          className="pick pick-secondary doctype-choice"
          disabled={busy}
          onClick={() => void choose(docType)}
        >
          {docTypeLabel(t, docType)}
          <span className="meta">{docTypeHint(t, docType)}</span>
        </button>
      ))}

      {/* A MENEKÜLŐÚT EGYENRANGÚ, NEM SZÉGYEN — a makett szaggatott kerettel
          és saját magyarázattal rajzolja. Kérés nem megy el tőle: a
          `clientDocType` marad `NULL`. */}
      <button
        type="button"
        className="pick pick-link doctype-unknown"
        disabled={busy}
        onClick={() => setOpen(false)}
      >
        {t('docType.unknown')}
        <span className="meta">{t('docType.unknownHint')}</span>
      </button>

      {/* ⚠️ A „MÉGSEM" NEM UGYANAZ, MINT A „NEM TUDOM" — pedig MA ugyanaz
          történik tőlük. (#26/#35)

          A hatás azonos: a választó bezárul, a `clientDocType` `NULL` marad.
          A KIMONDOTT DOLOG viszont más. A „Nem tudom" ÁLLÍTÁS — a makett
          ezért rajzolja egyenrangúra, saját magyarázattal —, a „Mégsem"
          pedig visszalépés: rossz gombot nyomtam. Aki véletlenül nyitotta
          meg a választót, ma kénytelen egy olyan gombot megnyomni, ami azt
          mondja róla, hogy nem tudja — pedig nem is akart nyilatkozni.

          Ezért NEM egy harmadik válasz, hanem halk link a válaszok ALATT,
          magyarázat nélkül. És ezért marad meg mindkettő: amikor a „Nem
          tudom" egyszer tényleg rögzít valamit (a könyvelőnek szóló jelzést),
          a kettő útja elválik — a „Mégsem" akkor sem fog rögzíteni semmit. */}
      <button
        type="button"
        className="pick pick-link doctype-cancel"
        disabled={busy}
        onClick={() => setOpen(false)}
      >
        {t('docType.cancel')}
      </button>

      {error === null ? null : (
        <span className="error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
